import React, { useState } from 'react';
import { 
  FaEye, 
  FaEyeSlash, 
  FaLock, 
  FaKey, 
  FaCheckCircle, 
  FaRegCircle, 
  FaShieldAlt,
  FaCheck,
  FaExclamationCircle
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import './ChangePassword.css';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // --- Real-time Validation Checks ---
  const hasLength = newPassword.length >= 6;
  const hasLower = /[a-z]/.test(newPassword);
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasDigit = /\d/.test(newPassword);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setIsError(false);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage('All fields are required.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!hasLength || !hasLower || !hasUpper || !hasDigit || !hasSpecial) {
      setMessage('Please ensure all password security requirements are met.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setMessage('New password and confirmation do not match.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/api/admin/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
      });

      setMessage(response.data.message || 'Password updated successfully.');
      setIsError(false);
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);

    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      setMessage(errorMsg);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Change Password | Admin Console</title>
        <meta
          name="description"
          content="Change and secure your administrative account password."
        />
      </Helmet>

      <div className="change-password-page-wrapper">
        {/* Subtle Background Pattern & Glow */}
        <div className="change-password-page-pattern"></div>
        <div className="change-password-page-glow"></div>

        <div className="change-password-page-card">
          {/* Top Gradient Accent Line */}
          <div className="change-password-page-accent-bar"></div>

          {/* --- Header with Symmetrical Eyebrow Dashes --- */}
          <div className="change-password-page-header">
            <div className="change-password-page-eyebrow">
              <span className="change-password-page-eyebrow-dash"></span>
              <span>Account Security</span>
              <span className="change-password-page-eyebrow-dash"></span>
            </div>
            
            <h2 className="change-password-page-title">Change Password</h2>
            <p className="change-password-page-subtitle">
              Update your administrative credentials with a strong security key.
            </p>
          </div>

          {/* Feedback Message Alert */}
          {message && (
            <div className={`change-password-page-message ${isError ? 'error' : 'success'}`}>
              {isError ? <FaExclamationCircle /> : <FaCheck />}
              <span>{message}</span>
            </div>
          )}

          {/* --- FORM --- */}
          <form onSubmit={handleSubmit} className="change-password-page-form">
            
            {/* Current Password */}
            <div className="change-password-page-input-group">
              <label className="change-password-page-label">Current Password</label>
              <div className="change-password-page-input-wrapper">
                <FaKey className="change-password-page-icon" />
                <input
                  className="change-password-page-input"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
                <button 
                  type="button"
                  className="change-password-page-eye-btn"
                  onClick={() => setShowCurrent(!showCurrent)}
                  aria-label={showCurrent ? "Hide password" : "Show password"}
                >
                  {showCurrent ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="change-password-page-input-group">
              <label className="change-password-page-label">New Password</label>
              <div className="change-password-page-input-wrapper">
                <FaLock className="change-password-page-icon" />
                <input
                  className="change-password-page-input"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <button 
                  type="button"
                  className="change-password-page-eye-btn"
                  onClick={() => setShowNew(!showNew)}
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="change-password-page-input-group">
              <label className="change-password-page-label">Confirm New Password</label>
              <div className="change-password-page-input-wrapper">
                <FaLock className="change-password-page-icon" />
                <input
                  className="change-password-page-input"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
                <button 
                  type="button"
                  className="change-password-page-eye-btn"
                  onClick={() => setShowConfirm(!showConfirm)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* --- Dynamic Password Requirements Checklist --- */}
            <div className="change-password-page-requirements-card">
              <div className="change-password-page-requirements-title">
                <FaShieldAlt /> Password Strength Checklist
              </div>
              <div className="change-password-page-requirements-grid">
                <span className={hasLength ? "req-met" : "req-unmet"}>
                  {hasLength ? <FaCheckCircle /> : <FaRegCircle />} Min 6 Chars
                </span>
                <span className={hasUpper ? "req-met" : "req-unmet"}>
                  {hasUpper ? <FaCheckCircle /> : <FaRegCircle />} 1 Uppercase
                </span>
                <span className={hasLower ? "req-met" : "req-unmet"}>
                  {hasLower ? <FaCheckCircle /> : <FaRegCircle />} 1 Lowercase
                </span>
                <span className={hasDigit ? "req-met" : "req-unmet"}>
                  {hasDigit ? <FaCheckCircle /> : <FaRegCircle />} 1 Number
                </span>
                <span className={hasSpecial ? "req-met" : "req-unmet"}>
                  {hasSpecial ? <FaCheckCircle /> : <FaRegCircle />} 1 Special Char
                </span>
                <span className={passwordsMatch ? "req-met" : "req-unmet"}>
                  {passwordsMatch ? <FaCheckCircle /> : <FaRegCircle />} Passwords Match
                </span>
              </div>
            </div>

            {/* Submit Button with Expanding Green Hover Effect */}
            <button 
              type="submit" 
              className="change-password-page-submit-btn" 
              disabled={isLoading}
            >
              <span>{isLoading ? 'Updating Credentials...' : 'Update Password'}</span>
            </button>
          </form>

        </div>
      </div>
    </>
  );
};

export default ChangePassword;